import React, { type ReactNode, type ReactElement, useMemo, memo } from 'react'
import { Layout as AntdLayout, App, ConfigProvider, type ThemeConfig } from 'antd'
import {
  useCompileMSTheme,
  useWppOsTheme,
  MSThemeContext,
  MSLayoutStylesheetComponent,
  type IMSTheme,
  type IMSStyleCfg,
} from '@choreograph/layout'

import { useAntdThemeConfig } from '../hooks/useAntdThemeConfig'
import { WppLayoutHeader } from './LayoutHeader'
import { WppLoadingSpinner } from './LoadingSpinner'
import { WppLayoutErrorBoundary } from './LayoutErrorBoundary'

import './styles/Layout.scss'

const { Content } = AntdLayout

const wppAgency = {
  agencyName: 'Wpp',
}

export interface IWppLayoutProps {
  appName: string
  children: ReactNode
  appLogo?: ReactElement
  appReady?: boolean
  extraClassName?: string
  themeConfig?: IMSTheme
  agencyStyleCfg?: IMSStyleCfg
  headerExtraContent?: ReactElement
}

export const WppLayout: React.FC<IWppLayoutProps> = memo(({
  appName,
  appLogo,
  children,
  appReady,
  themeConfig,
  agencyStyleCfg,
  extraClassName,
  headerExtraContent
}) => {
  const { theme: wppOsTheme } = useWppOsTheme(true, wppAgency, document.body, true)
  const currentTheme = useCompileMSTheme(true, wppAgency, 'WppOs', wppOsTheme, themeConfig, document.body)
  const antThemeTokensCfg = useMemo((): ThemeConfig => {
    if (currentTheme && currentTheme.data) {
      const { data: { color, text, typography } } = currentTheme
      const headingFontSize = typography['2xl'].heading.fontSize

      return {
        components: {
          Button: {
            fontWeight: 600,
            colorBgContainerDisabled: color.primary[300],
            colorTextDisabled: color.grey['400'],
          },
          Table: {
            rowSelectedBg: color.grey[200],
            rowHoverBg: color.grey[200],
            rowSelectedHoverBg: color.grey[400],
            headerBg: color.grey['000'],
            headerColor: text.color.default
          },
          Notification: {
            colorBgElevated: color.grey[900],
            colorText: color.grey['000'],
            colorTextHeading: color.grey['000'],
            colorIcon: color.grey['000']
          },
          Form: {
            itemMarginBottom: 36,
            labelColor: color.grey[800],
            fontWeightStrong: 600,
          },
          Select: {
            optionSelectedColor: color.primary[500],
            optionSelectedBg: color.primary[100]
          },
          Drawer: {
            fontSizeLG: headingFontSize
          }
        }
      }
    }
    return {}
  }, [currentTheme])

  const antThemeConfig = useAntdThemeConfig(currentTheme, antThemeTokensCfg)

  return (
    <MSThemeContext.Provider value={currentTheme}>
      <ConfigProvider theme={antThemeConfig}>
        <App>
          <MSLayoutStylesheetComponent config={agencyStyleCfg} />
          <AntdLayout className={`wpp-layout__container ${extraClassName ? extraClassName : ''}`}>
            <WppLayoutErrorBoundary>
              <WppLayoutHeader
                appName={appName}
                appLogo={appLogo}
                extraContent={headerExtraContent}
              />
              <Content>
                {
                  appReady
                    ? children
                    : <WppLoadingSpinner />
                }
              </Content>
            </WppLayoutErrorBoundary>
          </AntdLayout>
        </App>
      </ConfigProvider>
    </MSThemeContext.Provider>
  )
})
